import { useState } from 'react';

import { LUMO_APP_NAME } from '@proton/shared/lib/constants';
import generateUID from '@proton/utils/generateUID';

import LogoBase, { type LogoProps } from './LogoBase';

const LumoLogo = ({ variant = 'with-wordmark', hasTitle = true, ...rest }: LogoProps) => {
    const [uid] = useState(generateUID('logo'));

    if (variant === 'glyph-only') {
        const logoWidth = 36;
        const logoHeight = 36;
        return (
            <LogoBase
                uid={uid}
                logoWidth={logoWidth}
                logoHeight={logoHeight}
                viewBox="0 0 36 36"
                title={hasTitle ? LUMO_APP_NAME : undefined}
                variant={variant}
                {...rest}
            >
                <path
                    fill="#6D4AFF"
                    d="m22.669 7.465 3.343-2.163c1.16-.75 2.698-.027 2.874 1.35l.81 6.348-7.029-5.535h.002ZM13.59 7.465l-3.344-2.163c-1.16-.75-2.698-.027-2.874 1.35L6.563 13l7.029-5.535h-.002Z"
                />
                <path
                    fill="#4A2DC5"
                    d="m9.322 7.853 1.623 1.052-2.647 1.792.32-2.515a.457.457 0 0 1 .702-.329h.002ZM26.69 7.853l-1.624 1.052 2.647 1.792-.32-2.515a.457.457 0 0 0-.702-.329h-.002Z"
                />
                <path fill="#4129A3" d="M27.017 25.377H8.98v4.756h18.037v-4.756Z" />
                <path
                    fill="#FC8F00"
                    d="M18.475 34.523c1.667 0 3.019-1.361 3.019-3.04 0-1.68-1.352-3.042-3.019-3.042s-3.019 1.362-3.019 3.041c0 1.68 1.352 3.041 3.02 3.041Z"
                />
                <path fill="#FC8F00" d="M17.35 34.525h1.156v-6.08H17.35v6.08Z" />
                <path
                    fill="#FFAC2E"
                    d="M17.347 34.523c1.667 0 3.018-1.361 3.018-3.04 0-1.68-1.351-3.042-3.018-3.042-1.667 0-3.019 1.362-3.019 3.041 0 1.68 1.352 3.041 3.019 3.041Z"
                />
                <path
                    fill="#372580"
                    d="M17.344 31.983a.942.942 0 0 0 .939-.946.942.942 0 0 0-.94-.946.942.942 0 0 0-.938.946c0 .522.42.946.939.946Z"
                />
                <path
                    stroke="#372580"
                    stroke-linecap="round"
                    stroke-miterlimit="10"
                    stroke-width=".96"
                    d="M17.344 32.52v-1.48"
                />
                <path
                    fill="#6D4AFF"
                    d="M32 19.653c0 6.48-7.633 8.789-14 8.789-5.596 0-14-2.31-14-8.79 0-6.479 6.268-13.028 14-13.028s14 6.55 14 13.029Z"
                />
                <path
                    fill="#fff"
                    d="M24.865 21.767c2.315-1.006 3.473-3.499 2.587-5.569-.886-2.07-3.48-2.932-5.796-1.926-2.315 1.005-3.473 3.499-2.587 5.568.886 2.07 3.481 2.933 5.796 1.927ZM14.44 19.839c.887-2.07-.272-4.563-2.587-5.569-2.315-1.006-4.91-.143-5.796 1.927-.886 2.07.272 4.563 2.587 5.569 2.315 1.005 4.91.143 5.797-1.927Z"
                />
                <path
                    fill="#4129A3"
                    d="M23.502 20.546c.844 0 1.528-1.123 1.528-2.508 0-1.386-.684-2.509-1.528-2.509s-1.528 1.123-1.528 2.509c0 1.385.684 2.508 1.528 2.508Z"
                />
                <path
                    fill="#372480"
                    d="M14.577 17.268c-1.272-3.114-5.925-4.304-8.017-1.437-.652.91-.81 2.087-.558 3.192-.89-1.68-.217-3.84 1.384-4.821 2.617-1.723 6.671-.06 7.19 3.067v-.001ZM18.934 17.267c.52-3.127 4.574-4.788 7.19-3.067 1.602.982 2.274 3.142 1.384 4.821.255-1.103.095-2.282-.558-3.192-2.092-2.867-6.744-1.677-8.016 1.437v.002Z"
                />
                <path
                    stroke="#fff"
                    stroke-linecap="round"
                    stroke-miterlimit="10"
                    stroke-width=".424"
                    d="M15.354 21.67c.512 0 .925.419.925.933"
                />
                <path
                    stroke="#fff"
                    stroke-linecap="round"
                    stroke-miterlimit="10"
                    stroke-width=".424"
                    d="M17.201 21.67a.928.928 0 0 0-.925.933"
                />
                <path
                    fill="#fff"
                    d="M24.43 16.983c.319 0 .577-.26.577-.58 0-.322-.259-.582-.577-.582a.579.579 0 0 0-.576.581c0 .32.258.581.576.581Z"
                    opacity=".7"
                />
                <path
                    fill="#4129A3"
                    d="M10.32 20.546c.845 0 1.529-1.123 1.529-2.508 0-1.386-.684-2.509-1.528-2.509s-1.528 1.123-1.528 2.509c0 1.385.684 2.508 1.528 2.508Z"
                />
                <path
                    fill="#fff"
                    d="M11.249 16.983c.318 0 .576-.26.576-.58 0-.322-.258-.582-.576-.582a.579.579 0 0 0-.577.581c0 .32.258.581.577.581Z"
                    opacity=".7"
                />
            </LogoBase>
        );
    }

    const logoWidth = 76;
    const logoHeight = 36;
    return (
        <LogoBase
            uid={uid}
            logoWidth={logoWidth}
            logoHeight={logoHeight}
            viewBox="0 0 76 36"
            title={hasTitle ? LUMO_APP_NAME : undefined}
            variant={variant}
            {...rest}
        >
            <path
                fill="var(--logo-text-proton-color)"
                d="M66.724 29.418c-1.783 0-3.383-.322-4.8-.966-1.417-.644-2.534-1.589-3.35-2.834-.816-1.246-1.224-2.76-1.224-4.542 0-1.76.408-3.264 1.224-4.51.816-1.266 1.933-2.233 3.35-2.898 1.418-.666 3.017-.999 4.8-.999 1.782 0 3.37.333 4.767.999 1.395.665 2.49 1.632 3.285 2.899.816 1.245 1.224 2.748 1.224 4.509 0 1.782-.408 3.296-1.224 4.542-.794 1.245-1.89 2.19-3.285 2.834-1.396.644-2.985.966-4.767.966Zm0-2.93c1.03 0 1.975-.205 2.834-.613a4.661 4.661 0 0 0 2.03-1.836c.515-.816.772-1.804.772-2.963 0-1.16-.257-2.147-.773-2.963a4.87 4.87 0 0 0-2.029-1.869c-.837-.45-1.782-.676-2.835-.676-1.052 0-2.007.215-2.866.644a5.313 5.313 0 0 0-2.094 1.869c-.515.815-.773 1.814-.773 2.995 0 1.16.258 2.147.773 2.963a4.863 4.863 0 0 0 2.062 1.836c.88.408 1.846.612 2.898.612ZM26.008 13.024h3.576v16.04h-3.576v-16.04Zm10.34-.355c.923 0 1.771.129 2.544.387.795.257 1.471.655 2.03 1.192.58.515 1.02 1.159 1.32 1.932.322.773.483 1.686.483 2.738v10.146H39.15v-9.438c0-1.31-.322-2.276-.966-2.898-.623-.645-1.61-.967-2.964-.967-1.03 0-1.975.226-2.834.677a5.947 5.947 0 0 0-2.062 1.707 4.377 4.377 0 0 0-.934 2.287l-.032-1.74a6.907 6.907 0 0 1 .709-2.254 6.717 6.717 0 0 1 1.45-1.933 6.424 6.424 0 0 1 2.125-1.353c.816-.322 1.718-.483 2.706-.483Zm12.787 0c.945 0 1.804.129 2.577.387a5.219 5.219 0 0 1 1.997 1.192c.58.515 1.03 1.159 1.353 1.932.322.773.483 1.686.483 2.738v10.146h-3.608v-9.438c0-1.31-.322-2.276-.966-2.898-.623-.645-1.61-.967-2.963-.967-1.031 0-1.976.226-2.835.677a5.947 5.947 0 0 0-2.061 1.707 4.515 4.515 0 0 0-.902 2.287l-.065-1.804a6.47 6.47 0 0 1 .741-2.223c.387-.73.88-1.374 1.482-1.932a6.799 6.799 0 0 1 2.093-1.32c.817-.323 1.708-.484 2.674-.484ZM23.348 29.064h-3.576v-16.04h3.576v16.04Zm-3.382-7.634.032 1.031c-.043.28-.14.698-.29 1.256-.129.559-.354 1.17-.676 1.836a8.216 8.216 0 0 1-1.224 1.868c-.494.58-1.117 1.063-1.869 1.45-.751.365-1.664.547-2.738.547a8.986 8.986 0 0 1-2.35-.322 6.04 6.04 0 0 1-2.127-.998c-.622-.473-1.127-1.117-1.514-1.933-.386-.816-.58-1.846-.58-3.092v-10.05h3.576v9.374c0 1.009.161 1.803.483 2.383.344.558.816.956 1.417 1.192.602.236 1.3.354 2.094.354 1.138 0 2.104-.257 2.899-.773a6.17 6.17 0 0 0 1.9-1.9c.494-.773.816-1.514.967-2.223ZM0 6.582h3.575v22.482H0V6.582Z"
            />
        </LogoBase>
    );

    return null;
};

export default LumoLogo;
